import { C as CYRILIC_LOCALES, h as isObject, I as INPUT_TYPE, c as clone } from './logger-faaf508a.js';

const INPUT = 'input';
const OUTPUT = 'output';
const SERVICE = 'service';
const REGULATORY = 'regulatory';
const FULL_SUMMARY = 'fullSummary';
const SHORT_SUMMARY = 'shortSummary';

const getContainerItems = (container) => {
  return container && container.items ? container.items : [];
};
const getContainerLabel = (container) => {
  return container && container.label ? container.label : '';
};
const isResponseValid = (response) => {
  if (!response ||
    !isObject(response) ||
    !response[INPUT] ||
    !response[OUTPUT]) {
    throw {
      error: response,
      code: 'ONE_CALC_ERROR',
      message: "Response is not a valid JSON object or doesn't contain input and output properties.",
    };
  }
};
const throwOutputRelatedError = () => {
  return (error, prop) => {
    throw {
      code: 'ONE_CALC_ERROR',
      message: `Trying to access ${prop} from output.containers: ${error}`,
    };
  };
};
const getContainerItemsById = (id, source, childContainerId) => {
  var _a;
  const container = getContainerById(id, source);
  if (container) {
    const hasChildContainerItems = childContainerId && !!((_a = container.childContainers) === null || _a === void 0 ? void 0 : _a.length);
    if (hasChildContainerItems) {
      const childContainer = container.childContainers.find((item) => item.id === childContainerId);
      return [...container.items, ...childContainer === null || childContainer === void 0 ? void 0 : childContainer.items];
    }
    return container.items || [];
  }
  return [];
};
const getContainerById = (id, source) => {
  return source.filter((c) => c.id === id)[0];
};
const getFootnoteIndex = (index, locale) => {
  const latinAlphabet = 'abcdefghijklmnopqrstuvwxyz';
  const cyrilicAlphabet = 'абвгдежзийклмнопрстуфхцчшщюя';
  if (CYRILIC_LOCALES.includes(locale)) {
    return cyrilicAlphabet[index];
  }
  return latinAlphabet[index];
};
const modifyInputs = (inputs) => {
  try {
    if (!Array.isArray(inputs)) {
      return [];
    }
    inputs.forEach((input) => {
      input.type = input.type.toUpperCase();
      if (input.type !== INPUT_TYPE.ONE_OF_MANY) {
        return;
      }
      if (input.step && input.minValue && input.maxValue && !input.values) {
        input.values = [];
        let currValue = Number(input.minValue.value);
        const maxValue = Number(input.maxValue.value);
        while (currValue <= maxValue) {
          input.values.push({
            value: String(currValue),
            label: String(currValue),
            selected: Number(input.value.value) === currValue,
            unit: input.value.unit,
            format: input.value.format,
            unitFormatted: input.unitFormatted,
          });
          currValue += Number(input.step);
        }
      }
      else if (input.values &&
        input.values.length > 1 &&
        !input.step &&
        !input.minValue &&
        !input.maxValue) {
        input.step =
          (Number(input.values[1].value) || 0) -
            (Number(input.values[0].value) || 0);
        input.minValue = Number(input.values[0].value) ? input.values[0] : null;
        input.maxValue = Number(input.values[input.values.length - 1].value)
          ? input.values[input.values.length - 1]
          : null;
      }
    });
    return inputs;
  }
  catch (error) {
    throw {
      code: 'FCIS_ERROR',
      message: `Something went wrong while manipulating inputs: ${error}`,
    };
  }
};
const getDisclaimerParams = (container) => {
  try {
    const items = getContainerItems(container);
    const disclaimerParams = items
      .filter((param) => param.disclaimer)
      .filter((param, index, array) => param.disclaimer &&
      array.findIndex((otherParam) => param.disclaimer === otherParam.disclaimer) === index);
    return disclaimerParams.sort((a, b) => a.disclaimerId < b.disclaimerId ? -1 : 0);
  }
  catch (error) {
    throwOutputRelatedError()(error, OUTPUT);
  }
};
function getParamValue(container) {
  const items = getContainerItems(container);
  return items.length ? items[0].value : null;
}
function getLabelText(container) {
  const items = getContainerItems(container);
  return items.length ? items[0].label : '';
}
/**
 * getContainersMap: Generates a key-value map of containers
 * and child containers to enable quick look-up without loops
 * @param containers - Output containers
 * @returns - An object mapping containers and child containers
 */
function getContainersMap(containers) {
  let containersMap = {};
  let childContainersMap = {};
  if (!Array.isArray(containers) || !containers.length) {
    return { containersMap, childContainersMap };
  }
  clone(containers).forEach((container) => {
    // Map containers
    containersMap[container.id] = container;
    // Map child containers
    if (container.childContainers) {
      container.childContainers.forEach((child) => {
        if (childContainersMap[container.id]) {
          childContainersMap[container.id][child.id] = child;
        }
        else {
          childContainersMap[container.id] = { [child.id]: child };
        }
      });
    }
  });
  return { containersMap, childContainersMap };
}
const addDisclaimerIndexToItems = (containers, containerId, locale) => {
  try {
    const outputItems = getContainerItemsById(containerId, containers, 'service');
    const itemsWithDisclaimers = outputItems.filter((item) => item.disclaimer);
    if (!itemsWithDisclaimers.length) {
      return;
    }
    const disclaimerWithIndex = [];
    let indexNumber = 0;
    itemsWithDisclaimers.forEach((item) => {
      const indexValue = disclaimerWithIndex.find((disclaimer) => disclaimer.content === item.disclaimer);
      if (indexValue) {
        item.disclaimerId = indexValue.id;
      }
      else {
        item.disclaimerId = getFootnoteIndex(indexNumber, locale);
        disclaimerWithIndex.push({
          id: getFootnoteIndex(indexNumber, locale),
          content: item.disclaimer,
        });
        indexNumber++;
      }
    });
  }
  catch (error) {
    throwOutputRelatedError()(error, OUTPUT);
  }
};

export { FULL_SUMMARY as F, OUTPUT as O, REGULATORY as R, SERVICE as S, getContainerItems as a, addDisclaimerIndexToItems as b, getContainersMap as c, getDisclaimerParams as d, getContainerLabel as e, getParamValue as f, getContainerItemsById as g, getLabelText as h, isResponseValid as i, SHORT_SUMMARY as j, getFootnoteIndex as k, modifyInputs as m };
